/**
 * CreateTab — the roster-builder UI inside a team picker column.
 *
 * Owns: team name + color, draggable slot list, randomize / add slot,
 * save handler. Color is optionally lifted to a parent via
 * `onColorChange` + `disabledColor` (used by LocalSetup so home and away
 * can't both pick the same color). When those props are omitted, color
 * stays purely local.
 *
 * Defaults to "gold" for the home team and "silver" for away — gives the
 * two columns a sensible starting contrast even when color coordination
 * isn't lifted to the parent.
 */

import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { Dices, Plus } from "lucide-react";
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import { NON_PITCHER_POSITIONS, positionRank } from "@/constants/positions";
import { TEAM_NAME_MAX, DEFAULT_SLOTS, MAX_SLOTS, MIN_SLOTS } from "@/constants/roster";
import { ColorPicker, SortableSlotRow } from "@/components/lobby/primitives";
import { PlayerDetailsDialog } from "@/components/game";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const makeSlot = (position, player = null) => ({
  uid: `${position}-${Math.random().toString(36).slice(2, 9)}`,
  position,
  player,
});

export default function CreateTab({
  gameId, teamType, onTeamSaved, stateRef, onStateChange,
  onColorChange, disabledColor = null,
}) {
  const [teamName, setTeamName] = useState("");
  // Home / away default to different starter colors so the two columns
  // read as distinct out of the box. Parent can still override via
  // onColorChange once the user touches the picker.
  const [teamColor, setTeamColor] = useState(teamType === "away" ? "silver" : "gold");
  const [allPlayers, setAllPlayers] = useState([]);
  const [slots, setSlots] = useState(() => DEFAULT_SLOTS.map(p => makeSlot(p)));
  const [saving, setSaving] = useState(false);
  const [previewPlayer, setPreviewPlayer] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  useEffect(() => {
    axios.get(`${API}/players/all`)
      .then(res => setAllPlayers(res.data))
      .catch(() => toast.error("Couldn't load player pool"));
  }, []);

  const pickRandomPlayer = useCallback((position, usedIds) => {
    if (!allPlayers.length) return null;
    if (!position) return null;          // unset slot — the caller must prompt
    const eligible = allPlayers.filter(p => {
      if (usedIds.has(p.id)) return false;
      if (position === "DH") return NON_PITCHER_POSITIONS.includes(p.position);
      return p.position === position;
    });
    if (!eligible.length) return null;
    return eligible[Math.floor(Math.random() * eligible.length)];
  }, [allPlayers]);

  const randomizeAll = useCallback(() => {
    if (!allPlayers.length) { toast.error("Player pool not loaded yet"); return; }
    const used = new Set();
    setSlots(prev => prev.map(slot => {
      const player = pickRandomPlayer(slot.position, used);
      if (player) used.add(player.id);
      return { ...slot, player };
    }));
  }, [allPlayers, pickRandomPlayer]);

  const rerollSlot = useCallback((uid) => {
    if (!allPlayers.length) return;
    setSlots(prev => {
      const used = new Set(prev.filter(s => s.uid !== uid && s.player).map(s => s.player.id));
      return prev.map(s => {
        if (s.uid !== uid) return s;
        const player = pickRandomPlayer(s.position, used);
        if (!player) {
          toast.error(s.position
            ? `No more ${s.position} players available`
            : "Pick a position for this slot first");
        }
        return { ...s, player };
      });
    });
  }, [allPlayers, pickRandomPlayer]);

  // Keep the roster grouped: pitchers, infield, outfield, DH. Sorting on every
  // position change (and on add) is what puts a new P with the other pitchers
  // instead of at the bottom of the list. Stable within a group, so ordering you
  // dragged inside a group survives.
  const sortByGroup = (arr) => [...arr].sort((a, b) => positionRank(a.position) - positionRank(b.position));

  const changePosition = (uid, pos) => setSlots(prev =>
    sortByGroup(prev.map(s => {
      if (s.uid !== uid) return s;
      // Drop a player the new position cannot legally field, so the slot is
      // ready to reroll into the position actually asked for rather than
      // keeping a mismatched player.
      const keeps = !s.player || pos === "DH"
        ? s.position !== "P" || pos === "P"
        : s.player.position === pos;
      return { ...s, position: pos, player: keeps ? s.player : null };
    })));
  const removeSlot = (uid) => setSlots(prev => prev.length > MIN_SLOTS ? prev.filter(s => s.uid !== uid) : prev);
  // New slots start UNSET. They used to default to "DH", which is not in the
  // dropdown's option list, so the control displayed "P" while the slot was
  // really DH — picking "P" was a no-op and the reroll drew from the DH pool
  // (any non-pitcher). An unset slot shows a placeholder and must be chosen.
  const addSlot = () => setSlots(prev => prev.length >= MAX_SLOTS ? prev : sortByGroup([...prev, makeSlot("")]));

  const handleDragEnd = ({ active, over }) => {
    if (active.id !== over?.id) {
      setSlots(prev => {
        const old = prev.findIndex(s => s.uid === active.id);
        const next = prev.findIndex(s => s.uid === over.id);
        return arrayMove(prev, old, next);
      });
    }
  };

  const filledSlots = slots.filter(s => s.player);
  const pitcherCount = filledSlots.filter(s => s.position === "P").length;
  const nonPitcherCount = filledSlots.filter(s => s.position !== "P").length;
  const canSave = teamName.trim() && pitcherCount >= 1 && nonPitcherCount >= 8;

  const handleSave = async () => {
    if (!canSave || !gameId) return false;
    setSaving(true);
    try {
      await axios.post(`${API}/games/${gameId}/teams/${teamType}/custom`, {
        name: teamName.trim(),
        color: teamColor,
        players: filledSlots.map(s => {
          const p = s.player;
          return {
            name: p.name,
            position: s.position,
            year: p.year || null,
            pitch_chips: p.pitch_chips || 4,
            bats: p.bats || null,
            throws: p.throws || null,
            stats: p.stats || {},
            modifiers: p.modifiers || {},
            active_batting_stats: p.active_batting_stats || [],
            active_pitching_stats: p.active_pitching_stats || [],
            card_condition: p.card_condition || "common",
            seasons: p.seasons || [],
          };
        }),
      });
      onTeamSaved(teamName.trim());
      setSaving(false);
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail ? `Failed to save team — ${detail}` : "Failed to save team");
      setSaving(false);
      return false;
    }
  };

  const canSaveNow = !!canSave;
  const hasStarted = !!teamName.trim() || filledSlots.length > 0;
  useEffect(() => {
    if (!stateRef) return;
    stateRef.current = { canSave: canSaveNow, hasStarted, save: handleSave };
  });
  // The parent's Save & Ready button lives outside this component, so typing
  // here never re-rendered it: it showed stale readiness until some unrelated
  // update arrived. Report changes so it re-renders.
  useEffect(() => {
    onStateChange?.({ canSave: canSaveNow, hasStarted });
  }, [onStateChange, canSaveNow, hasStarted]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <input
            placeholder="Team name"
            value={teamName}
            onChange={e => setTeamName(e.target.value)}
            maxLength={TEAM_NAME_MAX}
            className="w-full px-3 py-2.5 rounded-xl bg-slate-800/80 border border-slate-700 text-white placeholder-slate-600 text-sm outline-none focus:ring-2 focus:ring-amber-500/40 transition-all pr-10"
          />
          {teamName.length > 0 && (
            <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-[9px] font-mono ${
              teamName.length >= TEAM_NAME_MAX ? "text-red-400" : "text-slate-600"
            }`}>
              {teamName.length}/{TEAM_NAME_MAX}
            </span>
          )}
        </div>
        <ColorPicker
          value={teamColor}
          onChange={(key) => { setTeamColor(key); onColorChange?.(key); }}
          disabledKey={disabledColor}
        />
      </div>

      <div className="grid grid-cols-2 gap-2">
        <button onClick={randomizeAll}
          className="flex items-center justify-center gap-1.5 py-2 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-xs transition-all">
          <Dices size={12} /> Randomize Roster
        </button>
        <button onClick={addSlot} disabled={slots.length >= MAX_SLOTS}
          className="flex items-center justify-center gap-1.5 py-2 rounded-xl border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white text-xs font-bold transition-all disabled:opacity-30 disabled:cursor-not-allowed">
          <Plus size={12} /> Add Slot ({slots.length}/{MAX_SLOTS})
        </button>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={slots.map(s => s.uid)} strategy={verticalListSortingStrategy}>
          <div className="space-y-1 max-h-72 overflow-y-auto pr-0.5">
            {slots.map((slot, idx) => {
              const prev = idx > 0 ? slots[idx - 1] : null;
              const showDivider = prev && (prev.position === "P") !== (slot.position === "P");
              return (
                <div key={slot.uid}>
                  {showDivider && (
                    <div className="flex items-center gap-2 px-1 py-1.5">
                      <div className="flex-1 h-px bg-slate-700/60" />
                      <span className="text-[8px] font-black uppercase tracking-widest text-slate-500">
                        {slot.position === "P" ? "Pitchers" : "Lineup"}
                      </span>
                      <div className="flex-1 h-px bg-slate-700/60" />
                    </div>
                  )}
                  <SortableSlotRow slot={slot}
                    onRemove={removeSlot} onPositionChange={changePosition} onReroll={rerollSlot}
                    onPreview={setPreviewPlayer}
                    canRemove={slots.length > MIN_SLOTS} />
                </div>
              );
            })}
          </div>
        </SortableContext>
      </DndContext>

      <PlayerDetailsDialog
        open={!!previewPlayer}
        onClose={() => setPreviewPlayer(null)}
        player={previewPlayer}
      />

      {!canSave && (
        <p className="text-[10px] text-slate-600 text-center">
          {!teamName.trim() ? "Enter a team name · " : ""}
          {pitcherCount < 1 ? "Need 1 pitcher · " : ""}
          {nonPitcherCount < 8 ? `Need ${8 - nonPitcherCount} more filled position players` : ""}
        </p>
      )}

      <button onClick={handleSave} disabled={!canSave || saving || !gameId}
        className="w-full py-2.5 rounded-xl bg-amber-500 hover:bg-amber-400 text-black font-black text-sm transition-all disabled:opacity-40">
        {saving ? "Saving…" : "Save Roster →"}
      </button>
    </div>
  );
}
